// Phase 59 — Idempotent transaction insert helper.
//
// Shared by every frontend INSERT path so we have ONE place that
// computes the fingerprint and guards against duplicates. Sits on top
// of supabase-js with PostgREST's onConflict semantics.
//
// Callers pass a row WITHOUT a fingerprint; this helper computes it
// from (user_id, account_id, txn_date, amount, description) and asks
// Postgres to ignore the insert if the unique index already covers it.

import { supabase } from '@/lib/supabaseClient';
import { transactionFingerprint } from '@/lib/dedup';

async function withFingerprint(row) {
  const fp = await transactionFingerprint({
    userId: row.user_id,
    accountId: row.account_id || null,
    txnDate: row.txn_date,
    amount: row.amount,
    description: row.description || '',
  });
  return { ...row, fingerprint: fp };
}

/**
 * Insert one transaction, idempotently.
 * Returns { inserted: 0|1, duplicate: boolean, error? }.
 *
 * If the row already exists for the user (same fingerprint), the call
 * is a no-op rather than raising — callers should treat the response
 * as success and surface a friendly "already in your ledger" message
 * if `duplicate` is true.
 *
 * Phase 84.19.2 — Belt-and-suspenders null-account guard.
 * The Postgres unique index `(user_id, account_id, fingerprint)` treats
 * NULL != NULL by default, so rows with `account_id = null` bypass
 * dedup entirely against rows with a real UUID account_id. That created
 * 141 orphan duplicates for a beta user. Refuse the write here so no
 * client-side code path can insert a null-account row when the user
 * already has ≥1 account. Callers can override by passing
 * `allowNullAccount: true` (used only by legitimate manual-entry paths
 * where the user genuinely hasn't set up an account yet).
 */
export async function insertTransactionIdempotent(row, opts = {}) {
  if (row?.account_id == null && !opts.allowNullAccount) {
    return {
      inserted: 0,
      duplicate: false,
      error: {
        message:
          'Refusing to insert transaction without an account. Please pick a target account before saving. (Phase 84.19.2 null-account guard.)',
        code: 'NULL_ACCOUNT_ID_BLOCKED',
      },
    };
  }
  const stamped = await withFingerprint(row);
  // Phase 59 — PostgREST upsert on the unique (user_id, account_id,
  // fingerprint) index. ignoreDuplicates=true means re-running the same
  // insert (network retry, double-clicked Save, two browser tabs) is a
  // true no-op. The .select() at the end lets us tell whether a row
  // was actually written or skipped.
  const { data, error } = await supabase
    .from('transactions')
    .upsert(stamped, {
      onConflict: 'user_id,account_id,fingerprint',
      ignoreDuplicates: true,
    })
    .select('id');
  if (error) {
    // If the unique index hasn't been deployed yet (schema migration
    // pending), gracefully fall back to a non-idempotent insert so the
    // user isn't blocked. Worst case: a duplicate slips through which
    // the dedup migration will catch.
    if (/fingerprint|on_conflict|conflict/i.test(error.message || '')) {
      // eslint-disable-next-line no-console
      console.warn('insertTransactionIdempotent: falling back to plain insert', error.message);
      const fallback = await supabase.from('transactions').insert(stamped).select('id');
      return {
        inserted: fallback.data?.length || 0,
        duplicate: false,
        error: fallback.error || null,
      };
    }
    return { inserted: 0, duplicate: false, error };
  }
  // ignoreDuplicates=true returns the row when inserted, [] when skipped.
  const inserted = (data || []).length;
  return { inserted, duplicate: inserted === 0, error: null };
}

/**
 * Bulk variant used by CSV import. Computes fingerprints in parallel
 * and upserts in CHUNK-sized batches.
 *
 * Phase 71.0 — within-batch deduplication.
 * PostgreSQL's INSERT ... ON CONFLICT DO NOTHING cannot resolve two
 * rows in the SAME statement that share the conflict target. If a CSV
 * contains two identical rows (e.g. duplicate $1,000 income on the
 * same date with the same description), upsert raises
 * "duplicate key value violates unique constraint
 * ux_transactions_user_acct_fingerprint" and the whole chunk fails.
 * We now collapse same-fingerprint duplicates inside each chunk
 * before sending it to the DB so the constraint cannot fire on
 * self-conflicts.
 */
export async function insertTransactionsBulkIdempotent(rows, { chunkSize = 50, allowNullAccount = false } = {}) {
  if (!rows || rows.length === 0) return { inserted: 0, duplicates: 0, errors: [] };
  // Phase 84.19.2 null-account guard. Strip rows with `account_id == null`
  // unless the caller explicitly opted in. This prevents the 141-orphan
  // pattern from ever recurring at the client boundary. Silently drop
  // them + report via `skipped_null_account` so callers can surface a
  // toast if they want. (We do NOT throw — bulk imports should still
  // succeed for the good rows.)
  let skipped_null_account = 0;
  let filteredRows = rows;
  if (!allowNullAccount) {
    filteredRows = [];
    for (const r of rows) {
      if (r?.account_id == null) {
        skipped_null_account += 1;
        continue;
      }
      filteredRows.push(r);
    }
    if (skipped_null_account > 0 && typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn(
        `[insertTransactionsBulkIdempotent] Dropped ${skipped_null_account} row(s) with account_id=null. ` +
        `Pass { allowNullAccount: true } if this was intentional. (Phase 84.19.2 guard.)`
      );
    }
  }
  const stamped = await Promise.all(filteredRows.map(withFingerprint));
  let inserted = 0;
  let duplicates = 0;
  const errors = [];
  for (let i = 0; i < stamped.length; i += chunkSize) {
    const sliceRaw = stamped.slice(i, i + chunkSize);
    // Collapse within-batch duplicates by fingerprint. Rows without a
    // fingerprint (very rare) flow through untouched so the migration
    // fallback below still works for them.
    const seen = new Set();
    let withinBatchDupes = 0;
    const slice = [];
    for (const row of sliceRaw) {
      const fp = row.fingerprint;
      if (fp && seen.has(fp)) {
        withinBatchDupes += 1;
        continue;
      }
      if (fp) seen.add(fp);
      slice.push(row);
    }
    duplicates += withinBatchDupes;

    const { data, error } = await supabase
      .from('transactions')
      .upsert(slice, {
        onConflict: 'user_id,account_id,fingerprint',
        ignoreDuplicates: true,
      })
      .select('id');
    if (error) {
      // Same migration-pending fallback as the single-row helper.
      if (/fingerprint|on_conflict|conflict/i.test(error.message || '')) {
        // eslint-disable-next-line no-console
        console.warn('insertTransactionsBulkIdempotent: falling back to plain insert', error.message);
        const fallback = await supabase.from('transactions').insert(slice).select('id');
        if (fallback.error) {
          // Phase 71.0 — translate raw PG unique-violation into a clean
          // application-level error so the toast doesn't leak the
          // constraint name. The caller decides whether to surface it.
          const friendly = makeFriendlyDedupError(fallback.error);
          errors.push(friendly || fallback.error);
        } else {
          inserted += fallback.data?.length || 0;
        }
      } else {
        // Phase 71.0 — even on the main upsert path, a raw unique-violation
        // can leak through (e.g. partial index conditions, NULL semantics).
        // Translate it instead of bubbling raw SQL to the UI.
        const friendly = makeFriendlyDedupError(error);
        errors.push(friendly || error);
      }
      continue;
    }
    const wrote = (data || []).length;
    inserted += wrote;
    duplicates += slice.length - wrote;
  }
  return { inserted, duplicates, errors, skipped_null_account };
}

/**
 * Translate a Postgres unique-violation error on the transaction
 * fingerprint index into a friendly, user-facing error object.
 * Returns null if the error doesn't look like a dedup collision.
 */
function makeFriendlyDedupError(err) {
  if (!err) return null;
  const msg = String(err.message || err.code || '').toLowerCase();
  const looksLikeDedup =
    msg.includes('ux_transactions_user_acct_fingerprint')
    || msg.includes('duplicate key value violates unique constraint')
    || err.code === '23505';
  if (!looksLikeDedup) return null;
  return {
    code: 'already_imported',
    friendly: true,
    message:
      "Some of these transactions are already in your ledger — we'll skip those and import the new ones. "
      + 'Try re-running the import; identical rows will be silently skipped this time.',
    original: err.message,
  };
}
